import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronIcon, PencilIcon } from '@/components/catalog/icons';
import { PermissionGrid } from '@/components/permissions/PermissionGrid';
import { PermissionSetFormModal } from '@/components/permissions/PermissionSetFormModal';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime, orDash, pluralise } from '@/lib/format';
import { revokesEverything, summarise, toItemsPayload } from '@/lib/permission-grid';
import { roleLabel } from '@/lib/roles';
import type {
  Permission,
  PermissionScope,
  PermissionSetDetail,
  User,
  UserRole,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { landingPageOptions } from '@/lib/use-landing-page';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  PageHeader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { usePageLayout } from '@/lib/use-page-layout';

/**
 * "N of M permissions granted". Every seeded set holds an explicit row for
 * every permission (migration 250), so `itemCount` alone reads the catalogue
 * size on every set. Against an API without the two fields, the old count.
 */
function grantedLabel(set: PermissionSetDetail): string {
  const { grantedCount, permissionTotal } = set;
  if (grantedCount === undefined || permissionTotal === undefined || permissionTotal <= 0) {
    return pluralise(set.itemCount, 'permission');
  }
  return `${grantedCount} of ${permissionTotal} permissions granted`;
}

/* --- constants ----------------------------------------------------------- */

/*
 * ✅ **THE CATALOGUE IS PAGED NOW, and the constant that used to sit here is
 * GONE.** *SUPERSEDED: `const CATALOGUE_PAGE = { page: 1, perPage: 100 }`, and
 * with it "the whole catalogue fits in ONE page … this list is never
 * paginated".* It stopped fitting: measured 2026-09-09 the live catalogue is
 * **98 rows across 25 modules**, and `dashboards-operations` takes it to
 * **102**.
 *
 * ⛔ **The fix was NOT a bigger number, and must never become one.** 100 is the
 * SERVER's ceiling — there is no larger value to ask for, and asking is a 422.
 * `api.listAllPermissions` reads page after page until the catalogue is
 * complete; its header carries the loop's terminators and the shared-rate-limit
 * cap that bounds it.
 *
 * ⚠️ **Why any of this mattered: the failure past 100 was SILENT.** No error,
 * no empty state, no pager — the grid stopped at row 100 and *looked complete*,
 * so a super-admin would configure a set against modules they were never shown.
 * Nothing on this screen could detect it, because a short page and a short
 * catalogue are the same response.
 *
 * 🔴 **So this page renders the catalogue or it renders a REFUSAL — never a
 * shorter grid.** A failed page two discards the pages already in hand and
 * surfaces the error, because partial data here is a false statement about what
 * a permission set grants, in the reassuring direction. See `loadCatalogue`.
 *
 * ⚠️ **Every number above is a measurement with a date on it, not a property**,
 * and the comment this replaces was wrong three times over — each figure
 * written while true and read long after. Re-measure with
 * `GET /permissions?perPage=1` and `meta.total`; nothing here reads a count,
 * which is exactly why five moves of it cost this file no code.
 */

/**
 * The five role slugs, in the order a person reads them.
 *
 * Used ONLY to order the probe below deterministically; membership itself is
 * derived from what the API answers, never from this list. A role added
 * server-side that is missing here still resolves, because the probe is driven
 * by the roles the STAFF LIST actually carries — `probeRoles` collects
 * `person.role` off the response and this array only sorts the result.
 */
const ROLE_ORDER: readonly UserRole[] = [
  'super_admin',
  'admin',
  'advanced_user',
  'basic_user',
  'viewer',
];

/* --- small shared bits --------------------------------------------------- */

function BackToSets({
  onNavigate,
}: {
  onNavigate: (event: { preventDefault: () => void }) => void;
}) {
  return (
    <Link
      to="/permission-sets"
      onClick={onNavigate}
      className="inline-flex items-center gap-1 rounded-sm text-xs text-fg-muted transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
    >
      <ChevronIcon direction="left" />
      All permission sets
    </Link>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function ErrorBanner({
  title,
  message,
  action,
}: {
  title: string;
  message: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Notice tone="danger" role="alert" className="m-4 mb-0" title={title} action={action}>
      <span className="[overflow-wrap:anywhere]">{message}</span>
    </Notice>
  );
}

/* --- grid state ---------------------------------------------------------- */

type ScopeMap = ReadonlyMap<number, PermissionScope>;

function toScopeMap(set: PermissionSetDetail): Map<number, PermissionScope> {
  /*
   * Keyed by permission id, which is what makes a duplicate `permissionId` in
   * the save payload structurally impossible — the server answers 422 naming
   * the repeated id, and a Map cannot produce one. The payload array below is
   * built by walking these entries and nothing else, so that holds end to end.
   */
  return new Map(set.items.map((item) => [item.permission.id, item.scope]));
}

/**
 * The dirty test.
 *
 * Compares id AND scope, not merely which rows are ticked: moving a permission
 * from `all` to `own` leaves the tick count identical and is a real, saveable
 * change.
 */
function sameGrid(a: ScopeMap, b: ScopeMap): boolean {
  if (a.size !== b.size) return false;
  for (const [permissionId, scope] of a) {
    if (b.get(permissionId) !== scope) return false;
  }
  return true;
}

/* --- loading shapes ------------------------------------------------------ */

/*
 * The real shape of the loaded page — details panel then grid panel — so the
 * layout does not jump sideways the instant the two requests land.
 */
function DetailsSkeleton() {
  return (
    <div className="grid min-w-0 gap-4">
      <Panel className="min-w-0">
        <PanelHeader title="Set details" />
        <PanelBody>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="grid gap-1.5">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-3.5 w-32" />
              </div>
            ))}
          </div>
        </PanelBody>
      </Panel>
      <Panel className="min-w-0">
        <PanelHeader title="Permissions" />
        <PanelBody>
          <div className="grid gap-2">
            {Array.from({ length: 10 }, (_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

/* --- page ---------------------------------------------------------------- */

type LoadResult =
  | { kind: 'ready'; set: PermissionSetDetail }
  | { kind: 'notfound' }
  | { kind: 'forbidden'; message: string }
  | { kind: 'error'; message: string };

interface MembersState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** The staff list — the first 100 by the API's own ordering. */
  users: User[];
  /**
   * role -> the ids of the set(s) that role derives.
   *
   * ⚠️ **Keyed by ROLE, not by user, and that is the whole change.** Since the
   * owner's 2026-09-04 decision a person's permissions come from their role
   * alone (`d_users.role` -> `f_role_default_sets` -> one set), so every user
   * of a given role resolves to the same set and asking each of them
   * separately asks the same question N times.
   *
   * A role ABSENT from this map is UNKNOWN — its probe failed — and its people
   * are rendered as unknown, never as non-members. `undefined` and `[]` are
   * different facts here: `[]` is a role the API says derives no set at all.
   *
   * The value stays an ARRAY rather than a single id. The contract says one
   * set today, and `GET /users/:id/permission-sets` still answers with a list;
   * flattening it to `id | null` here would make a two-element answer read as
   * the first element, silently.
   */
  setIdsByRole: Map<UserRole, number[]>;
  /** Distinct roles present in the staff list, for the progress line. */
  rolesToProbe: number;
  /** How many role probes have come back, for the progress line. */
  resolved: number;
  /** `meta.total` from the user list — over 100 staff means a partial view. */
  totalStaff: number;
  error: string | null;
  /** Set when a probe came back 429. Kept: the probe is small, not free. */
  rateLimited: boolean;
  /** Roles whose set could not be read. Their people show as unknown. */
  unresolvedRoles: UserRole[];
}

const EMPTY_MEMBERS: MembersState = {
  status: 'idle',
  users: [],
  setIdsByRole: new Map(),
  rolesToProbe: 0,
  resolved: 0,
  totalStaff: 0,
  error: null,
  rateLimited: false,
  unresolvedRoles: [],
};

export function PermissionSetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { ability } = useAuth();

  /*
   * SUPER-ADMIN ONLY, and gated on `ability.isSuperAdmin` rather than
   * `isAdmin`. The API itself allows any admin here; this app is deliberately
   * stricter, so an ordinary admin must not even issue the reads.
   */
  /*
   * 🔴 2026-10-01: the `permission-sets` module's READ (only the super-admin set
   * holds it today). *SUPERSEDED, the comment above: `ability.isSuperAdmin`.*
   * The local name is kept so the gates below read unchanged; the write
   * controls also ask `permission-sets.update` through `canEditSet`.
   */
  const isSuperAdmin = ability.canRead('permission-sets');
  const canEditSet = ability.canUpdate('permission-sets');

  /*
   * The per-person layout, called above every early return. Defaults reproduce
   * the page as it was: four full-width panels in this order. "Landing page"
   * renders nothing against an API without `landingPath`, as before. Ids are
   * stored per user — never rename one.
   */
  const layout = usePageLayout('permission-set', [
    { id: 'details', label: 'Set details' },
    { id: 'landing', label: 'Landing page' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'members', label: 'Who holds this set' },
  ]);

  /*
   * A non-numeric segment can never match a row, so it is a 404 without asking
   * the API — which would answer 422 for it, and "/permission-sets/banana"
   * does not mean a validation error to anybody.
   */
  const setId = id !== undefined && /^\d+$/.test(id) ? Number(id) : null;

  const [result, setResult] = useState<{ id: number; value: LoadResult } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [catalogue, setCatalogue] = useState<{
    rows: Permission[] | null;
    error: string | null;
  } | null>(null);

  /*
   * In flight, and it earns its keep now that the catalogue is MULTI-REQUEST.
   * It is not a second loading state for the panel — `rows === null` already
   * draws the skeletons — it is what stops the "Try again" button firing a
   * second paged read over the top of the first. Each one is up to ten requests
   * against a bucket shared across the whole office — and two overlapping paged
   * reads race each other into the same state, which is the half that breaks.
   * ⚠️ *SUPERSEDED: "a bucket of 120 per 60 seconds."* It is **12,000/60s**
   * (corrected 2026-09-17).
   */
  const [catalogueLoading, setCatalogueLoading] = useState(false);

  /* The committed grid (what the server last told us) and the working copy.
     Both null until the set has loaded. */
  const [baseline, setBaseline] = useState<ScopeMap | null>(null);
  const [draft, setDraft] = useState<Map<number, PermissionScope> | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [pendingClear, setPendingClear] = useState(false);
  const [editing, setEditing] = useState(false);
  const [leaveTo, setLeaveTo] = useState<string | null>(null);

  /*
   * The landing-page draft, and `undefined` is a THIRD state rather than a
   * missing one: it means "no local edit — follow whatever the server last
   * said". `null` is a real, chosen value meaning this set names no landing
   * page.
   *
   * That is what lets an unsaved landing pick survive a save of the permission
   * grid beside it. Both write to the same record and both refresh state from
   * the response, so a draft held as a plain value would be silently overwritten
   * by an unrelated save — the person picks a page, saves permissions, and the
   * picker snaps back with a 200 and nothing to explain it. With `undefined` the
   * follow-the-server case needs no reset at all, so there is no reset to fire
   * at the wrong moment.
   */
  const [landingDraft, setLandingDraft] = useState<string | null | undefined>(undefined);
  const [landingSaving, setLandingSaving] = useState(false);
  const [landingError, setLandingError] = useState<string | null>(null);

  const [members, setMembers] = useState<MembersState>(EMPTY_MEMBERS);
  const membersAbort = useRef<AbortController | null>(null);

  const applyDetail = useCallback((detail: PermissionSetDetail, keepDraft: boolean) => {
    const committed = toScopeMap(detail);
    setResult({ id: detail.id, value: { kind: 'ready', set: detail } });
    setBaseline(committed);
    /*
     * The save response is the whole set read back INSIDE the write
     * transaction, so it is exactly what committed — including the ids and
     * `createdAt` of the rows the diff left untouched. Resetting local state
     * from it is what makes the page genuinely clean rather than optimistic.
     */
    setDraft((previous) => (keepDraft && previous !== null ? previous : new Map(committed)));
  }, []);

  const loadSet = useCallback(
    (signal?: AbortSignal, keepDraft = false) => {
      if (setId === null) return Promise.resolve();
      const settle = (value: LoadResult) => setResult({ id: setId, value });
      return api
        .getPermissionSet(setId, signal)
        .then((response) => {
          if (signal?.aborted) return;
          applyDetail(response.data, keepDraft);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 404) {
            settle({ kind: 'notfound' });
            return;
          }
          if (caught instanceof ApiError && caught.status === 403) {
            /*
             * A 403 body carries NO `details` array — that is the 422 shape —
             * so there is nothing to hang on a field and it reads as a banner.
             */
            settle({ kind: 'forbidden', message: caught.message });
            return;
          }
          settle({
            kind: 'error',
            message:
              caught instanceof Error ? caught.message : 'Failed to load this permission set',
          });
        })
        .finally(() => {
          if (signal?.aborted) return;
          setRefreshing(false);
        });
    },
    [setId, applyDetail],
  );

  /*
   * The WHOLE catalogue, however many pages that takes — `api.listAllPermissions`
   * reads until it is complete and throws rather than resolving short.
   *
   * 🔴 **A FAILURE ON ANY PAGE DISCARDS THE PAGES ALREADY IN HAND.** `rows`
   * goes back to `null`, which is the same state as "not loaded yet", so the
   * panel renders the error banner and skeletons — never a grid.
   *
   * That is the point of the whole change and it is worth stating plainly:
   * rendering the pages that DID arrive would be a grid that stops early and
   * looks complete, which is a false statement about what this set grants. It
   * fails in the reassuring direction — nothing errors, nothing is empty, the
   * modules are simply not there — so nobody would report it, and a super-admin
   * would tick boxes believing they had seen every module. **A visible refusal
   * beats a plausible half-answer.**
   */
  const loadCatalogue = useCallback((signal?: AbortSignal) => {
    setCatalogueLoading(true);
    return api
      .listAllPermissions(signal)
      .then((rows) => {
        if (signal?.aborted) return;
        setCatalogue({ rows, error: null });
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        setCatalogue({
          rows: null,
          error:
            caught instanceof Error ? caught.message : 'Failed to load the permission catalogue',
        });
      })
      .finally(() => {
        if (signal?.aborted) return;
        setCatalogueLoading(false);
      });
  }, []);

  useEffect(() => {
    /* No fetch at all below super-admin. The explanatory state further down
       renders from the role alone and never touches the network. */
    if (!isSuperAdmin || setId === null) return;
    const controller = new AbortController();
    void loadSet(controller.signal);
    void loadCatalogue(controller.signal);
    return () => controller.abort();
  }, [isSuperAdmin, setId, loadSet, loadCatalogue]);

  useEffect(() => () => membersAbort.current?.abort(), []);

  const current = result && result.id === setId ? result.value : null;
  const set = current?.kind === 'ready' ? current.set : null;

  const dirty = baseline !== null && draft !== null && !sameGrid(baseline, draft);

  /*
   * ⚠️ **`undefined` MEANS THE API HAS NO SUCH FIELD, AND IT IS THE WHOLE GATE
   * ON THE LANDING-PAGE CONTROL.** This app deploys separately from the API and
   * can be newer than it, so `landingPath` is optional on the read type: a
   * server that serves the key sends `string | null`, and a server that does not
   * sends nothing at all. Those are different facts and folding them together is
   * what would break this — reading the absence as "this set names none" offers
   * a picker whose only possible outcome is a `422 Unrecognized key:
   * "landingPath"` from a `.strict()` update schema, which is the
   * affordance-with-one-outcome this app refuses everywhere else.
   *
   * So against today's API the panel is simply not rendered and this page looks
   * exactly as it did. It appears on its own the moment the backend ships the
   * field, with no deploy here.
   */
  const landingSupported = set !== null && set.landingPath !== undefined;
  const landingStored = set?.landingPath ?? null;
  const landingValue = landingDraft === undefined ? landingStored : landingDraft;
  const landingDirty = landingDraft !== undefined && landingDraft !== landingStored;

  /*
   * The browser half of the unsaved-changes guard. The in-app back link is
   * intercepted separately below: this app mounts a `BrowserRouter`, and
   * `useBlocker` works only under a data router, so it is not available here.
   *
   * ⚠️ **`landingDirty` is in here as well as `dirty`.** The two panels save
   * separately, so "unsaved" is not one flag — and an unsaved landing pick is
   * exactly as easy to lose as an unsaved grid edit, while being a single
   * dropdown nobody would think to check before navigating away.
   */
  const anythingUnsaved = dirty || landingDirty;

  useEffect(() => {
    if (!anythingUnsaved) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anythingUnsaved]);

  /* ── saving the grid ───────────────────────────────────────────────── */

  async function saveGrid() {
    if (setId === null || draft === null) return;
    setSaving(true);
    setSaveError(null);
    setConflict(null);
    try {
      /*
       * FULL REPLACE, never a diff: the final state of every row the set
       * should end up with. `items: []` is legal and clears the set outright.
       * Built by walking the Map's entries, so no `permissionId` can repeat.
       *
       * ⚠️ Since 2026-08-30 this carries the DENIALS as well as the grants —
       * unticking a box writes `scope: 'none'` rather than dropping the row —
       * so a set that denies everything sends one row per catalogue permission
       * (94 today) instead of an empty array. The server diffs internally, so
       * the rows that did not change keep their id and `createdAt`; the schema
       * puts no cap on the array and the body limit is 1MB against a payload of
       * a few kilobytes.
       */
      const items = toItemsPayload(draft);
      const response = await api.replacePermissionSetItems(setId, { items });
      applyDetail(response.data, false);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        /* Somebody else changed this set between our read and our write.
           Surfaced verbatim as a reload-and-retry prompt, not a dead end. */
        setConflict(caught.message);
      } else if (caught instanceof ApiError && caught.status === 403) {
        setSaveError(
          'Your access changed since this page loaded, so nothing was saved. Permission sets are super-admin only in this app.',
        );
      } else if (caught instanceof ApiError && caught.status === 422) {
        /* 422 is the only status carrying `details`. It names the offending
           rows — a repeated permissionId, an id not in the catalogue — so the
           paths are worth showing next to the message. */
        const detail = (caught.details ?? [])
          // An empty path is the whole-object failure a `.strict()` schema
          // reports; prefixing it would print a bare leading colon.
          .map((entry) => (entry.path ? `${entry.path}: ${entry.message}` : entry.message))
          .join('; ');
        setSaveError(detail ? `${caught.message} (${detail})` : caught.message);
      } else if (caught instanceof ApiError && caught.status === 404) {
        setSaveError('This permission set no longer exists — someone else deleted it.');
      } else {
        setSaveError(caught instanceof Error ? caught.message : 'Could not save these permissions');
      }
    } finally {
      setSaving(false);
    }
  }

  function requestSave() {
    if (!dirty || draft === null || baseline === null) return;
    /*
     * Taking every grant off a set is a legal, one-request, entirely
     * non-obvious wipe, so it gets a confirmation. Every other save does not.
     *
     * ⚠️ The test is "the draft GRANTS nothing", not "the draft is empty". The
     * empty version was correct until unticking started writing `none`, and
     * that change made it **unreachable** — nothing removes a row any more, so
     * a draft can no longer shrink to empty from a set that had rows, and the
     * confirmation would have quietly stopped firing on the single gesture it
     * exists for. `revokesEverything` still covers the empty case.
     */
    if (revokesEverything(draft, baseline)) {
      setPendingClear(true);
      return;
    }
    void saveGrid();
  }

  function reloadSet(keepDraft: boolean) {
    setConflict(null);
    setSaveError(null);
    setRefreshing(true);
    void loadSet(undefined, keepDraft);
  }

  /* ── the landing page this set names ───────────────────────────────── */

  /*
   * ⚠️ **`undefined` MEANS THE API HAS NO SUCH FIELD, AND IT IS THE WHOLE GATE
   * ON THIS CONTROL.** This app deploys separately from the API and can be newer
   * than it, so `landingPath` is optional on the read type: a server that serves
   * the key sends `string | null`, and a server that does not sends nothing at
   * all. Those are different facts and folding them together is what would break
   * this — reading the absence as "this set names none" offers a picker whose
   * only possible outcome is a `422 Unrecognized key: "landingPath"` from a
   * `.strict()` update schema, which is the affordance-with-one-outcome this app
   * refuses everywhere else.
   *
   * So against today's API the panel is simply not rendered and this page looks
   * exactly as it did. It appears on its own the moment the backend ships the
   * field, with no deploy here.
   */
  /*
   * The four derived values live UP with `dirty`, not here, because the
   * leave guard reads `landingDirty` and that runs in an effect above.
   *
   * What a super admin may choose from.
   *
   * ⚠️ **Derived from `ability` — which is THE ADMINISTRATOR'S, not the
   * holders'.** There is no way to ask for a hypothetical person's nav, and a
   * super admin sees every row, so this is the widest list rather than the
   * intersection of what holders can open. That is deliberate and it is safe:
   * `resolveLandingPathWithSet` re-tests reachability at sign-in and falls
   * through when a holder cannot open the page, so the worst case is a set whose
   * landing page does not apply to some of its holders — never somebody landing
   * on a refusal they cannot navigate out of.
   *
   * ⚠️ **A stored value that is no longer a page is INJECTED BACK, labelled**,
   * rather than dropped. A `<select>` handed a value none of its options carry
   * shows the first option instead, so dropping it would silently display the
   * wrong answer AND arm the next save to write it — the same silent
   * reassignment this codebase guards against on every retired-master picker.
   */
  const landingOptions = useMemo(() => {
    const options = landingPageOptions(ability);
    if (landingStored === null || options.some((option) => option.path === landingStored)) {
      return options;
    }
    return [...options, { path: landingStored, label: `${landingStored} — no longer a page here` }];
  }, [ability, landingStored]);

  async function saveLandingPath() {
    if (setId === null || !landingDirty || landingDraft === undefined) return;
    setLandingSaving(true);
    setLandingError(null);
    try {
      /*
       * ONE key. `PATCH /permission-sets/:id` needs at least one field and
       * touches nothing it is not sent, so this cannot disturb the name, slug or
       * status — and `null` is the documented clear.
       *
       * `keepDraft: true` is load-bearing: the response is the whole set, items
       * included, and applying it with `false` would throw away an unsaved
       * permission-grid edit as a side effect of saving a landing page.
       */
      const response = await api.updatePermissionSet(setId, { landingPath: landingDraft });
      applyDetail(response.data, true);
      /* Back to following the server, so the picker reads from the saved
         record rather than from a draft that now merely agrees with it. */
      setLandingDraft(undefined);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setLandingError(
          'Your access changed since this page loaded, so nothing was saved. Permission sets are super-admin only in this app.',
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        setLandingError('This permission set no longer exists — someone else deleted it.');
      } else if (caught instanceof ApiError && caught.status === 422) {
        /*
         * `formMessage`, not `fieldErrors`. An unrecognised key on a `.strict()`
         * schema arrives with an EMPTY path and the name under `key`, so there
         * is no control to mark and routing it as a field error would render it
         * nowhere at all. This is also the one 422 worth expecting here: it is
         * what an API that does not yet accept `landingPath` answers.
         */
        setLandingError(caught.formMessage ?? caught.message);
      } else {
        setLandingError(
          caught instanceof Error ? caught.message : 'Could not save the landing page',
        );
      }
    } finally {
      setLandingSaving(false);
    }
  }

  /* ── members ───────────────────────────────────────────────────────── */

  /*
   * WHO HOLDS THIS SET — DERIVED FROM ROLES, ONE PROBE PER ROLE.
   *
   * 🔴 **MEMBERSHIP IS NO LONGER ASSIGNED, SO IT IS NO LONGER READ PER USER.**
   * Owner's decision 2026-09-04: a person's permissions come from their role
   * alone — `d_users.role` -> `f_role_default_sets` -> exactly one set — and
   * `PUT /users/:id/permission-sets` is retired (409
   * `USER_PERMISSION_SETS_DERIVED_FROM_ROLE`). So "who holds this set" is
   * answerable from the staff list plus the role->set mapping, and that mapping
   * has at most five entries however many people there are.
   *
   * ⚠️ *SUPERSEDED, the whole cost paragraph that stood here: "deriving the
   * members of one set costs one request for the staff list plus ONE REQUEST PER
   * MEMBER OF STAFF … 11 members of staff = 12 requests … 10% of everybody's
   * budget per visit … `members.totalStaff > members.users.length` IS THE POINT
   * AT WHICH THIS MUST GO BACK TO BEING OPT-IN."* **That ceiling is gone.** The
   * cost is now `1 + (distinct roles present)`, bounded at **6**, and it does not
   * move when the company hires. 101 members of staff cost 6 requests, not 101,
   * so nothing here has to become opt-in again.
   *
   * ── WHY THERE IS STILL A PROBE AT ALL ────────────────────────────────────
   *
   * ⚠️ **The role->set mapping is NOT in anything this client already holds.**
   * `PermissionSetSummary` carries `slug`, `name`, `isSystem`, `isActive`,
   * `sortOrder`, `itemCount` and `landingPath` — and **no role**. Nothing on
   * `GET /permission-sets` says which role points at a set, so the mapping has
   * to be asked for. `GET /users/:id/permission-sets` still answers it, now
   * returning the single role-derived set, so ONE representative user per
   * distinct role establishes the whole table.
   *
   * ⛔ **Do NOT close this by matching set slugs against role slugs.** Set slugs
   * are admin-renameable and are being renamed right now; a client that guessed
   * `admin` -> the set slugged `administrator` would be inventing a join key the
   * server never offered, which is this codebase's free-text-read-as-a-flag trap.
   * The probe reads the answer instead of assuming it.
   *
   * **What would remove the probe entirely: a role field on the permission-set
   * row.** One key — the role slug a set is the default for — would make this
   * panel cost exactly one request, the staff list. It does not exist today.
   *
   * ── FAILURE IS PER ROLE, AND STAYS HONEST ────────────────────────────────
   *
   * A failed probe leaves that ROLE out of `setIdsByRole`, so its people render
   * as unknown rather than as non-members — the same rule the per-user sweep
   * followed, one level up. `holderCount` still refuses to report a number
   * unless every role resolved and the staff list was complete.
   */
  const loadMembers = useCallback(async () => {
    membersAbort.current?.abort();
    const controller = new AbortController();
    membersAbort.current = controller;
    const { signal } = controller;

    setMembers({ ...EMPTY_MEMBERS, status: 'loading', setIdsByRole: new Map() });

    let staff: User[] = [];
    let totalStaff = 0;
    try {
      const response = await api.listUsers({ perPage: 100 }, signal);
      staff = response.data;
      totalStaff = response.meta.total;
    } catch (caught) {
      if (signal.aborted) return;
      setMembers({
        ...EMPTY_MEMBERS,
        status: 'error',
        error: caught instanceof Error ? caught.message : 'Failed to load the staff list',
      });
      return;
    }
    if (signal.aborted) return;

    /*
     * The roles actually present, each with one person to ask about.
     *
     * Driven by the response rather than by `ROLE_ORDER`, so a role slug this
     * build has never heard of is still probed and still resolves — and a role
     * nobody holds costs nothing. `ROLE_ORDER` only sorts the result for the
     * progress line.
     */
    const probeRoles = new Map<UserRole, User>();
    for (const person of staff) {
      if (!probeRoles.has(person.role)) probeRoles.set(person.role, person);
    }
    const probes = [...probeRoles.entries()].sort(
      ([a], [b]) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b),
    );

    setMembers((previous) => ({
      ...previous,
      users: staff,
      totalStaff,
      rolesToProbe: probes.length,
    }));

    /*
     * All at once. There are at most five, which is inside the concurrency the
     * per-user sweep used to allow itself, and a batching loop over five items
     * would be machinery with nothing to protect against.
     */
    const settled = await Promise.allSettled(
      probes.map(([, person]) => api.listUserPermissionSets(person.id, signal)),
    );
    if (signal.aborted) return;

    const setIdsByRole = new Map<UserRole, number[]>();
    const unresolvedRoles: UserRole[] = [];
    let rateLimited = false;

    settled.forEach((outcome, index) => {
      const entry = probes[index];
      if (!entry) return;
      const [role] = entry;
      if (outcome.status === 'fulfilled') {
        setIdsByRole.set(
          role,
          outcome.value.data.map((row) => row.permissionSet.id),
        );
      } else {
        unresolvedRoles.push(role);
        if (outcome.reason instanceof ApiError && outcome.reason.status === 429) {
          rateLimited = true;
        }
      }
    });

    setMembers((previous) => ({
      ...previous,
      status: 'ready',
      setIdsByRole,
      resolved: probes.length,
      unresolvedRoles,
      rateLimited,
    }));
  }, []);

  /*
   * Run the sweep on arrival, and again when the id in the URL changes.
   *
   * ⚠️ **`setId` is in the deps for a real reason, not for tidiness.** This page
   * is reachable from one set to another without unmounting, and the members
   * state is keyed to no set at all — `holders` filters `setIdsByRole` by
   * whatever `setId` currently is. Without the re-run, a second set would render
   * the first set's reading, which is not stale data but data about a different
   * record, and the panel gives the reader nothing to notice it by.
   *
   * The cleanup aborts whatever is in flight. `loadMembers` also aborts the
   * previous controller as its first statement, so a re-run is safe from both
   * ends; belt and braces here because the unmount case has no re-run to do it.
   */
  useEffect(() => {
    if (!isSuperAdmin || setId === null) return;
    /*
     * oxlint traces `loadMembers` and sees a synchronous `setMembers` in it,
     * and it is right: the state IS reset to `loading` before the first await,
     * deliberately. The alternative is a Refresh press that leaves the previous
     * set's rows on screen while the new sweep runs — a stale list presented as
     * a current one, which is the failure this whole change exists to remove.
     *
     * This is the case the rule's own help text exempts: an effect
     * synchronising React with an external system. Suppressed rather than
     * deferred behind a microtask, because a `queueMicrotask` here would be a
     * change made to a linter rather than to the program, and the next reader
     * would have no way to tell which.
     */
    // oxlint-disable-next-line react/set-state-in-effect
    void loadMembers();
    return () => membersAbort.current?.abort();
  }, [isSuperAdmin, setId, loadMembers]);

  /*
   * The members: everybody whose ROLE derives this set.
   *
   * ⚠️ **A role missing from `setIdsByRole` is UNKNOWN, not excluded** — its
   * probe failed, and guessing "not a member" would understate the headcount on
   * the one screen where a floor must never read as a total. Those people fall
   * into `unknownStaff` below and are counted, not listed.
   */
  const holders = useMemo(() => {
    if (setId === null) return [];
    return members.users.filter((person) => members.setIdsByRole.get(person.role)?.includes(setId));
  }, [members, setId]);

  /*
   * ⚠️ `unknownStaff` — staff whose role never resolved — was DELETED with the
   * note that reported it (2026-09-05). They are still excluded from `holders`
   * below, so the members list is still short by that many people; nothing
   * counts them and nothing says so.
   */

  /*
   * A headcount is only reported when every role resolved AND the staff list was
   * complete. A run that was rate-limited, that lost a role, or that only got the
   * first 100 of a longer staff list produces a number that is quietly too low —
   * and "3 people hold this" is a far worse thing to tell somebody about to edit
   * a system set than "not known yet". Null is the honest answer.
   */
  const holderCount =
    members.status === 'ready' &&
    !members.rateLimited &&
    members.unresolvedRoles.length === 0 &&
    members.totalStaff <= members.users.length
      ? holders.length
      : null;

  /*
   * ⚠️ `derivingRoles` — which roles resolve to this set — was DELETED with the
   * members-footer sentence that named them (2026-09-05). It was read off the
   * same map as `holders`, so the naming and the rows could never disagree; if
   * a sentence ever names those roles again, derive it from `setIdsByRole` and
   * not from `ROLE_ORDER.filter(...)`, which silently omits a role slug this
   * build has never heard of while `holders` still counts its people.
   */

  /*
   * "Nobody has answered yet", which is now a moment rather than a state.
   *
   * `idle` used to mean "and nobody is going to, until you press something".
   * With the sweep running on mount it means "the effect has not fired yet" —
   * one render — so it is folded in with `loading` everywhere a reader sees it.
   * The member is NOT removed from `MembersState`: it is still the honest
   * starting value, and a state machine whose initial state is a lie about what
   * has been read is how a panel comes to claim an empty list it never fetched.
   */
  const headcountPending = members.status === 'idle' || members.status === 'loading';

  /* ── guards ────────────────────────────────────────────────────────── */

  if (!isSuperAdmin) {
    return (
      <>
        <PageHeader title="Permission sets" />
        <Panel className="min-w-0">
          <EmptyState
            title="Only a super admin can open this"
            description="Only super admins can open permission sets."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
                Back to the dashboard
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (setId === null) {
    return (
      <>
        <PageHeader
          eyebrow={<BackToSets onNavigate={() => undefined} />}
          title="Permission set not found"
        />
        <Panel className="min-w-0">
          <EmptyState
            title="That isn’t a permission set reference"
            description="A permission set id is a positive whole number."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/permission-sets')}>
                All permission sets
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToSets onNavigate={() => undefined} />} title="Permission set" />
        <DetailsSkeleton />
      </>
    );
  }

  if (current.kind === 'notfound') {
    return (
      <>
        <PageHeader
          eyebrow={<BackToSets onNavigate={() => undefined} />}
          title="Permission set not found"
        />
        <Panel className="min-w-0">
          <EmptyState
            title="This permission set is gone"
            description="It has been deleted, or this link is from another environment."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/permission-sets')}>
                All permission sets
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current.kind === 'forbidden') {
    return (
      <>
        <PageHeader eyebrow={<BackToSets onNavigate={() => undefined} />} title="Permission set" />
        <Panel className="min-w-0">
          {/* A real 403 from the server. It has no `details` — only a 422 does
              — so there is nothing to attach to a field and it is a banner. */}
          <ErrorBanner title="You can’t open this permission set." message={current.message} />
          <PanelBody>
            <Button variant="secondary" size="sm" onClick={() => navigate('/permission-sets')}>
              All permission sets
            </Button>
          </PanelBody>
        </Panel>
      </>
    );
  }

  if (current.kind === 'error' || set === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToSets onNavigate={() => undefined} />} title="Permission set" />
        <Panel className="min-w-0">
          <ErrorBanner
            title="Couldn’t load this permission set."
            message={current.kind === 'error' ? current.message : 'Unknown error'}
            action={
              <Button variant="secondary" size="sm" onClick={() => reloadSet(false)}>
                Try again
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  const catalogueRows = catalogue?.rows ?? null;
  const catalogueError = catalogue?.error ?? null;
  const gridBusy = saving || refreshing;
  const loadedSet = set;

  function guardedLeave(event: { preventDefault: () => void }, to: string) {
    if (!anythingUnsaved) return;
    event.preventDefault();
    setLeaveTo(to);
  }

  /*
   * The same guard for a BUTTON, which has no navigation to prevent.
   *
   * The members panel's "Go to Users" links off this page, and the unsaved grid
   * and landing-page edits are exactly as losable through it as through the back
   * link above. A second exit that skips the confirmation would make the guard
   * read as unreliable rather than absent, which is worse.
   */
  function leaveFor(to: string) {
    if (anythingUnsaved) {
      setLeaveTo(to);
      return;
    }
    navigate(to);
  }

  return (
    <>
      <PageHeader
        eyebrow={<BackToSets onNavigate={(event) => guardedLeave(event, '/permission-sets')} />}
        title={loadedSet.name}
        description={
          <>
            <span className="font-mono text-xs">{loadedSet.slug}</span>
            <span className="text-fg-subtle">·</span>
            {loadedSet.isSystem ? (
              <Badge tone="neutral" variant="outline">System set</Badge>
            ) : (
              <Badge tone="neutral" variant="outline">Custom set</Badge>
            )}
            {loadedSet.isActive ? (
              <Badge tone="success" dot>
                Active
              </Badge>
            ) : (
              <Badge tone="warning" dot>
                Retired
              </Badge>
            )}
            <span className="text-fg-subtle">·</span>
            <span>{grantedLabel(loadedSet)}</span>
          </>
        }
        actions={
          <>
            {canEditSet && (
              <Button variant="secondary" leadingIcon={<PencilIcon />} onClick={() => setEditing(true)}>
                Edit details
              </Button>
            )}
            <LayoutToolbar layout={layout} />
          </>
        }
      />

      {/*
        `min-w-0` on the grid and on every panel in it is load-bearing, not
        decoration. A grid item defaults to min-width:auto, so a wide
        permission grid widens the whole track instead of scrolling inside its
        own container, and the page overflows sideways at 320px.
      */}
      <LayoutGrid
        layout={layout}
        render={{
          details: () => (
        <Panel className="min-w-0" aria-labelledby="set-details-heading">
          <PanelHeader
            id="set-details-heading"
            title="Set details"
            description={`Created ${formatDateTime(loadedSet.createdAt)}`}
          />
          <PanelBody>
            <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
              <DetailItem label="Name">{loadedSet.name}</DetailItem>
              <DetailItem label="Slug">
                <span className="font-mono text-xs">{loadedSet.slug}</span>
              </DetailItem>
              <DetailItem label="Permissions granted">{grantedLabel(loadedSet)}</DetailItem>
              {/* The Status hint — "Retiring a set does not take it off the
                  roles that derive it." — was removed on 2026-09-05, and
                  `DetailItem` lost its `hint` prop with it. A reader who takes
                  "Retired" to mean access was revoked is no longer corrected. */}
              <DetailItem label="Status">
                {loadedSet.isActive ? 'Active' : 'Retired'}
              </DetailItem>
              <DetailItem label="Description">{orDash(loadedSet.description)}</DetailItem>
              <DetailItem label="Sort order">{loadedSet.sortOrder}</DetailItem>
              <DetailItem label="Created by">
                {loadedSet.createdBy ? loadedSet.createdBy.name : '—'}
              </DetailItem>
              <DetailItem label="Last modified by">
                {loadedSet.modifiedBy ? loadedSet.modifiedBy.name : '—'}
                <span className="mt-0.5 block text-xs text-fg-muted">
                  {loadedSet.updatedAt === loadedSet.createdAt
                    ? 'Not changed since it was created'
                    : formatDateTime(loadedSet.updatedAt)}
                </span>
              </DetailItem>
            </dl>
          </PanelBody>
        </Panel>
          ),

        /*
          THE LANDING PAGE THIS SET NAMES.

          Rendered only when the API serves the field — see `landingSupported`.

          It is its own panel rather than a ninth cell in "Set details" above,
          because that panel is a read-only `<dl>` of facts and this is a control
          with its own save, its own refusal and its own dirty state. Dropping an
          editable field into a definition list would make one of eight
          identical-looking values behave unlike the other seven, which is the
          asymmetry-inside-one-form problem this codebase records elsewhere.
        */
          landing: () =>
            landingSupported ? (
          <Panel className="min-w-0" aria-labelledby="set-landing-heading">
            <PanelHeader id="set-landing-heading" title="Landing page" />

            {landingError !== null && (
              <ErrorBanner title="Couldn’t save the landing page." message={landingError} />
            )}

            <PanelBody>
              <div className="max-w-md min-w-0">
                <Field label="Where holders of this set land after signing in">
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={landingValue ?? ''}
                      disabled={landingSaving}
                      onChange={(event) => {
                        setLandingError(null);
                        /* '' is the "No landing page" row, and it writes an
                           explicit null — the documented clear — never an empty
                           string, which this API would store as a value. */
                        const next = event.target.value;
                        setLandingDraft(next === '' ? null : next);
                      }}
                    >
                      {/*
                        Not a placeholder: a real, selectable choice that clears
                        the field. A set naming no landing page is the ordinary
                        state — most sets will never name one — and it must be
                        reachable rather than only being where you start.
                      */}
                      <option value="">No landing page</option>
                      {landingOptions.map((option) => (
                        <option key={option.path} value={option.path}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
            </PanelBody>

            <PanelFooter>
              {/*
                ⚠️ BOTH FOOTER SENTENCES REMOVED 2026-09-05. They said that a
                person's OWN landing-page preference wins over this setting, and
                that a change lands at their next sign-in. The first is the one
                that will now be diagnosed as a bug: an administrator sets this,
                asks a colleague who has chosen their own landing page to sign
                in, watches nothing change, and concludes the control is broken.
                The losing case is invisible from both screens.
              */}
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {landingDirty && <span className="text-xs text-fg-muted">Unsaved change</span>}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!landingDirty || landingSaving}
                  onClick={() => {
                    setLandingError(null);
                    setLandingDraft(undefined);
                  }}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!landingDirty || !canEditSet}
                  loading={landingSaving}
                  onClick={() => void saveLandingPath()}
                  {...(landingDirty
                    ? {}
                    : { title: 'Nothing has changed since this set was loaded.' })}
                >
                  Save landing page
                </Button>
              </div>
            </PanelFooter>
          </Panel>
            ) : null,

        /*
          ⚠️ THE SYSTEM-SET NOTICE IS GONE — removed 2026-09-05 as a headcount
          note. It sat above the grid on purpose: the seeded sets are what every
          role resolves to, so whoever is about to tick something got the
          headcount BEFORE they reached a control. That warning no longer
          renders; `holderCount` survives and is still named in the "Deny every
          permission" confirmation, which is now the only place a reader is told
          how many people a change reaches — and only at the last step.
        */
          permissions: () => (
        <Panel
          className="min-w-0"
          aria-labelledby="set-grid-heading"
          aria-busy={gridBusy || undefined}
        >
          <PanelHeader
            id="set-grid-heading"
            title="Permissions"
            description={
              catalogueRows === null
                ? catalogueError !== null
                  ? 'The catalogue couldn’t be loaded'
                  : 'Loading the catalogue…'
                : /* GRANTED, not `draft.size` — that is the number of rows the
                     set holds, and since unticking writes a denial a row is no
                     longer evidence of access. A set can hold every permission
                     in the catalogue and grant none of them. */
                  `${pluralise(catalogueRows.length, 'permission')} in the catalogue · ${
                    draft === null ? 0 : summarise(draft).granting
                  } granted by this set`
            }
          />

          {catalogueError !== null && (
            <ErrorBanner
              title="Couldn’t load the permission catalogue."
              message={catalogueError}
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  loading={catalogueLoading}
                  onClick={() => {
                    setCatalogue(null);
                    void loadCatalogue();
                  }}
                >
                  Try again
                </Button>
              }
            />
          )}

          {conflict !== null && (
            <Notice
              tone="warning"
              role="alert"
              className="m-4 mb-0"
              title="Somebody else changed this set while you were editing."
            >
              <div className="flex flex-col items-start gap-2">
                {/* Verbatim: the server's message names what collided, and
                    paraphrasing it loses exactly the useful part. */}
                <span className="min-w-0 [overflow-wrap:anywhere]">{conflict}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  Nothing was saved. Reload, then save again.
                </span>
                <div className="flex flex-wrap gap-2">
                  <Button variant="primary" size="sm" onClick={() => reloadSet(true)}>
                    Reload and keep my changes
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => reloadSet(false)}>
                    Reload and discard my changes
                  </Button>
                </div>
              </div>
            </Notice>
          )}

          {saveError !== null && (
            <ErrorBanner title="Couldn’t save these permissions." message={saveError} />
          )}

          <PanelBody>
            {catalogueRows === null || draft === null ? (
              <div className="grid gap-2">
                {Array.from({ length: 10 }, (_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (
              /* `min-w-0` and nothing else: the grid is sectioned rows that wrap
                 rather than a wide table, and this wrapper must refuse to be
                 widened by its child so the page never scrolls sideways at
                 320px. */
              <div
                className={cx(
                  'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
                  gridBusy && 'opacity-60',
                )}
              >
                <PermissionGrid
                  catalogue={catalogueRows}
                  value={draft}
                  onChange={(next) => {
                    setSaveError(null);
                    setDraft(next);
                  }}
                  disabled={gridBusy || !canEditSet}
                />
              </div>
            )}
          </PanelBody>

          <PanelFooter>
            {/*
              The two things that are true of every save on this page, said
              next to the button that performs it.
            */}
            {/* The enforcement statement STAYS, reworded for the strict model.
                SUPERSEDED 2026-10-01: "Changes what people see in this app, not
                what the API allows." The server now enforces the set. */}
            <div className="min-w-0 text-xs text-fg-muted">
              <p className="m-0 [overflow-wrap:anywhere]">
                Changes what people can <strong className="font-semibold">see and do</strong> — the
                server enforces it.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {dirty && <span className="text-xs text-fg-muted">Unsaved changes</span>}
              <Button
                variant="secondary"
                size="sm"
                disabled={!dirty || saving}
                onClick={() => setDraft(baseline === null ? null : new Map(baseline))}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!dirty || catalogueRows === null || !canEditSet}
                loading={saving}
                onClick={requestSave}
                {...(dirty ? {} : { title: 'Nothing has changed since this set was loaded.' })}
              >
                Save permissions
              </Button>
            </div>
          </PanelFooter>
        </Panel>
          ),

          /* ── members ─────────────────────────────────────────────────── */
          members: () => (
        <Panel
          className="min-w-0"
          aria-labelledby="set-members-heading"
          aria-busy={headcountPending || undefined}
        >
          <PanelHeader
            id="set-members-heading"
            title="Who holds this set"
            description={
              headcountPending
                ? members.rolesToProbe === 0
                  ? 'Reading the staff list…'
                  : `Reading ${members.resolved} of ${pluralise(members.rolesToProbe, 'role')}…`
                : members.status === 'error'
                  ? 'Couldn’t be loaded'
                  : `${pluralise(holders.length, 'person', 'people')} of ${pluralise(
                      members.users.length,
                      'staff member',
                    )}`
            }
            actions={
              /*
                A REFRESH, not a loader — the derivation runs on arrival.
                It re-reads a list that can be minutes old by the time somebody
                is looking at it: a role change made on `/users`, in this session
                or another admin's, moves who appears here. Disabled while a run
                is in flight, because pressing it then would abort the run in
                progress and restart it from zero — visibly slower, for no new
                data.
              */
              <Button
                variant="secondary"
                size="sm"
                disabled={headcountPending}
                loading={headcountPending}
                onClick={() => void loadMembers()}
              >
                Refresh
              </Button>
            }
          />

          {members.status === 'error' && (
            <ErrorBanner
              title="Couldn’t load the members."
              message={members.error ?? 'Unknown error'}
              action={
                <Button variant="secondary" size="sm" onClick={() => void loadMembers()}>
                  Try again
                </Button>
              }
            />
          )}

          {/* `idle` renders the skeleton too. It lasts one render — until the
              mount effect fires — and an empty panel for that frame would read
              as "nobody holds this", which is the exact wrong answer this
              change exists to stop. */}
          {headcountPending && (
            <PanelBody>
              <div className="grid gap-2">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            </PanelBody>
          )}

          {members.status === 'ready' && (
            <>
              {members.rateLimited && (
                <Notice
                  tone="warning"
                  role="alert"
                  className="m-4 mb-0"
                  title="The rate limit refused part of this."
                >
                  <span className="[overflow-wrap:anywhere]">
                    {pluralise(members.unresolvedRoles.length, 'role')} couldn’t be read, so this
                    list is short. Wait a minute, then refresh.
                  </span>
                </Notice>
              )}

              {/*
                ⚠️ TWO HEADCOUNT NOTES REMOVED 2026-09-05 — "Showing the first N
                of M staff." and "N staff members couldn't be checked, so they
                are not listed either way." The rate-limit ALERT above survives
                (it is a refusal), but a list shortened by paging or by an
                unreadable role now reads as a complete one.
              */}
              {holders.length === 0 ? (
                <EmptyState
                  title="Nobody holds this set"
                  /*
                    An EmptyState description is a sanctioned exception, and it
                    is doing the exception's job: without it a super admin looks
                    for the Add control that used to be here and concludes the
                    page is broken. It names the thing that DOES decide this —
                    a person's role, on a page they can reach.
                  */
                  description={
                    loadedSet.isSystem
                      ? 'No role points at this set today. New accounts are provisioned by role.'
                      : 'No role points at this set. Membership follows a person’s role, set on Users.'
                  }
                  action={
                    <Button variant="secondary" size="sm" onClick={() => leaveFor('/users')}>
                      Go to Users
                    </Button>
                  }
                />
              ) : (
                <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                  {/*
                    Two columns now, not four. "Sets held" would read `1` down
                    every row — a derived set is the only one anybody holds — and
                    the Actions column held the Remove button, which is gone: see
                    the footer. `min-w-` drops with them.
                  */}
                  <Table className="min-w-[360px]">
                    <THead>
                      <TR>
                        <TH>Person</TH>
                        <TH className="w-48">Role</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {holders.map((person) => (
                        <TR key={person.id}>
                          <TH scope="row">
                            <span className="flex min-w-0 items-center gap-2">
                              <Avatar name={person.name} size="sm" />
                              <span className="min-w-0">
                                <span className="block [overflow-wrap:anywhere]">{person.name}</span>
                                <span className="block text-xs [overflow-wrap:anywhere]">
                                  <EmailLink address={person.email} tone="muted" />
                                </span>
                              </span>
                            </span>
                          </TH>
                          <TD>
                            <Badge tone="neutral" variant="outline">{roleLabel(person.role)}</Badge>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </TableWrap>
              )}

              <PanelFooter>
                {/*
                  THE ADD AND REMOVE CONTROLS ARE GONE, NOT DISABLED.

                  `PUT /users/:id/permission-sets` is retired — it answers 409
                  `USER_PERMISSION_SETS_DERIVED_FROM_ROLE` — so a control here
                  could only ever produce a refusal, and this app removes those
                  rather than disabling them (the Xero `sync` precedent).

                  What replaces them is a sentence and a link, and it is not
                  helper text by this repo's own test: it names the state a
                  reader cannot otherwise see. An administrator arriving at a
                  members list with no way to change it concludes the page is
                  broken and goes hunting; without this they have nothing to
                  find, because the control they want is on another page
                  entirely and is not called "permission sets".
                */}
                {/*
                  ⚠️ REMOVED 2026-09-05: "Membership follows a person's role …
                  Change it on their user record." and the next-sign-in line.
                  The Add and Remove controls are still gone (the endpoint
                  answers 409 `USER_PERMISSION_SETS_DERIVED_FROM_ROLE`), so an
                  administrator now meets a members list with no way to change
                  it and nothing naming the control that does — only the
                  unlabelled "Go to Users" button beside this.
                */}
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => leaveFor('/users')}>
                    Go to Users
                  </Button>
                </div>
              </PanelFooter>
            </>
          )}
        </Panel>
          ),
        }}
      />

      <PermissionSetFormModal
        open={editing}
        onClose={() => setEditing(false)}
        set={loadedSet}
        /*
         * PATCH returns the whole set, items included, so the committed grid is
         * refreshed from the response — but an unsaved draft is NOT thrown away
         * by a rename. `dirty` is recomputed against the new baseline either way.
         */
        onSaved={(saved) => {
          setEditing(false);
          applyDetail(saved, dirty);
        }}
      />

      <ConfirmDialog
        open={pendingClear}
        onClose={() => setPendingClear(false)}
        onConfirm={() => {
          setPendingClear(false);
          void saveGrid();
        }}
        title={`Deny every permission in ${loadedSet.name}?`}
        description={
          <>
            {/*
              This used to read "removes all N permissions from the set". It was
              accurate until unticking started writing an explicit denial, and
              then it described the opposite of what the button does: the rows
              are not removed, they are kept and turned into refusals. Saying
              "removes" would understate it — a removed row is silence, and on a
              dashboard module silence still means visible.
            */}
            <p className="m-0">
              Writes an explicit deny for{' '}
              {pluralise(draft === null ? 0 : draft.size, 'permission')}, granting none of them. The
              set itself stays.
            </p>
            <p className="m-0 mt-2">
              {holderCount === null
                ? 'Holders get nothing from it, from their next sign-in.'
                : holderCount === 0
                  ? 'Nobody holds this set today.'
                  : `${pluralise(holderCount, 'person', 'people')} ${
                      holderCount === 1 ? 'holds' : 'hold'
                    } it and will get nothing from it, from their next sign-in.`}
            </p>
          </>
        }
        confirmLabel="Deny all permissions"
        loading={saving}
      />

      {/*
        THE "Remove from set" CONFIRMATION IS GONE with the control that opened
        it. Nothing on this page writes a person's sets any more — the only
        write left is the permission grid, which writes the SET, not its
        holders.
      */}

      <ConfirmDialog
        open={leaveTo !== null}
        onClose={() => setLeaveTo(null)}
        onConfirm={() => {
          const to = leaveTo;
          setLeaveTo(null);
          if (to) navigate(to);
        }}
        title="Leave without saving?"
        /* Named per panel rather than as one word: the two save separately, so
           "your changes" would leave somebody unsure which of the two they are
           about to lose — and on a page where one of them is a single dropdown,
           that is the half they will not have thought about. */
        description={
          dirty && landingDirty
            ? 'Your permission changes and the landing page haven’t been saved. Leaving discards them.'
            : landingDirty
              ? 'The landing page hasn’t been saved. Leaving discards it.'
              : 'Your permission changes haven’t been saved. Leaving discards them.'
        }
        confirmLabel="Discard and leave"
        cancelLabel="Stay here"
      />
    </>
  );
}
